import React, { useMemo, useState, useEffect, useRef } from 'react';
import { CalendarClock, Users, Clock, Plus, Target, ListTodo, BookOpen, CalendarPlus, NotebookPen, Check, Loader2, CalendarRange, Repeat, ChevronRight } from 'lucide-react';
import { GoalRow, NoteRow, SectionTitle, ItemChip, LinkedTasksBadge } from './PlannerBits';
import { ParticipantAvatars } from './GoalShareBits';
import { timeToMin, fmtTimeRange, prettyDateLong, todayStr } from './plannerUtils';
import { Textarea } from '@/components/ui/textarea';
import PlannerMiniCalendar from './PlannerMiniCalendar';
import { RichTaskCard } from './TaskCardKit';

const HOUR_PX = 64;
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

/* Planner-native per-day note (distinct from Notes-module notes). Autosaves. */
function DayNoteCard({ date, initial, onSave }) {
  const [text, setText] = useState(initial || '');
  const [status, setStatus] = useState('idle'); // idle | saving | saved
  const savedRef = useRef(initial || '');

  useEffect(() => { setText(initial || ''); savedRef.current = initial || ''; setStatus('idle'); }, [date, initial]);

  useEffect(() => {
    if (text === savedRef.current) return;
    setStatus('saving');
    const h = setTimeout(async () => {
      try { await onSave(date, text); savedRef.current = text; setStatus('saved'); }
      catch { setStatus('idle'); }
    }, 700);
    return () => clearTimeout(h);
  }, [text, date, onSave]);

  const flush = async () => {
    if (text === savedRef.current) return;
    setStatus('saving');
    try { await onSave(date, text); savedRef.current = text; setStatus('saved'); }
    catch { setStatus('idle'); }
  };

  return (
    <div className="rounded-[var(--p-radius-lg)] border border-[color:var(--p-border)] bg-[color:var(--p-card)] p-4 shadow-[var(--p-shadow)]" data-testid="planner-day-note-panel">
      <SectionTitle icon={NotebookPen} action={
        <span className="flex items-center gap-1 text-[11px] text-[color:var(--p-muted-2)]" data-testid="planner-day-note-status">
          {status === 'saving' && <><Loader2 size={12} className="animate-spin" /> Saving</>}
          {status === 'saved' && <><Check size={12} className="text-[color:var(--p-success)]" /> Saved</>}
        </span>
      }>Day note</SectionTitle>
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={flush}
        rows={4}
        placeholder="Jot a note for this day — a reflection, a reminder, anything…"
        data-testid="planner-day-note-textarea"
        className="resize-none border-[color:var(--p-border)] bg-[color:var(--p-card-2)] text-[13.5px] text-[color:var(--p-text)] placeholder:text-[color:var(--p-muted-2)]"
      />
    </div>
  );
}

/* All-day & multi-day events for the day, shown ABOVE the Schedule. Multi-day
   spans carry a "Day X of N" context chip. Click opens the event dialog. */
function AllDayStrip({ events, onOpenItem }) {
  if (!events || events.length === 0) return null;
  return (
    <div className="mb-4 rounded-[var(--p-radius-lg)] border border-[color:var(--p-border)] bg-[color:var(--p-card)] shadow-[var(--p-shadow)]" data-testid="planner-daily-allday-strip">
      <div className="flex items-center gap-2 border-b border-[color:var(--p-line)] px-4 py-2.5">
        <CalendarRange size={15} className="text-[color:var(--p-accent)]" />
        <span className="text-[11px] font-semibold uppercase tracking-[0.13em] text-[color:var(--p-muted-2)]">All-day</span>
      </div>
      <div className="flex flex-col gap-1.5 p-2.5">
        {events.map((e) => (
          <button
            key={`${e.id}-${e.start}`}
            type="button"
            onClick={() => onOpenItem(e)}
            data-testid="planner-daily-allday-item"
            className="group relative flex items-center gap-2.5 overflow-hidden rounded-[10px] border border-[color:var(--p-accent-soft)] bg-[color:var(--p-accent-bg)] px-3 py-2 text-left transition-colors hover:bg-[color:var(--p-accent-bg)]/80"
          >
            <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-[color:var(--p-accent)]" aria-hidden="true" />
            <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-[color:var(--p-text)]">{e.title}</span>
            <LinkedTasksBadge tasks={e.tasks} />
            <ParticipantAvatars item={e} size={16} max={3} />
            {e.recurring && <Repeat size={12} className="shrink-0 text-[color:var(--p-accent)]" aria-label="Repeats" />}
            {e.span_total > 1 && (
              <span className="shrink-0 rounded-full bg-[color:var(--p-card)] px-2 py-0.5 text-[10.5px] font-bold text-[color:var(--p-accent)]" data-testid="planner-allday-day-context">
                Day {e.span_index} of {e.span_total}
              </span>
            )}
            <ChevronRight size={14} className="shrink-0 text-[color:var(--p-muted-2)] transition-transform group-hover:translate-x-0.5" />
          </button>
        ))}
      </div>
    </div>
  );
}

function Timeline({ events, meetings, onOpenItem, onNewEvent, dateStr, isToday }) {
  const items = [...(events || []), ...(meetings || [])];
  const timed = items.filter((i) => i.start_time).sort((a, b) => timeToMin(a.start_time) - timeToMin(b.start_time));
  const untimed = items.filter((i) => !i.start_time);

  let startH = 8; let endH = 18;
  timed.forEach((i) => {
    const s = timeToMin(i.start_time);
    const e = i.end_time ? timeToMin(i.end_time) : s + 60;
    startH = Math.min(startH, Math.floor(s / 60));
    endH = Math.max(endH, Math.ceil(e / 60));
  });
  startH = clamp(startH, 0, 22); endH = clamp(endH, startH + 2, 24);
  const hours = []; for (let h = startH; h <= endH; h++) hours.push(h);

  // greedy lane packing for overlapping items
  const laneEnds = []; const placed = timed.map((i) => {
    const s = timeToMin(i.start_time);
    const e = Math.max((i.end_time ? timeToMin(i.end_time) : s + 60), s + 30);
    let lane = laneEnds.findIndex((end) => end <= s);
    if (lane === -1) { lane = laneEnds.length; laneEnds.push(e); } else { laneEnds[lane] = e; }
    return { i, s, e, lane };
  });
  const laneCount = Math.max(1, laneEnds.length);
  const spanMin = startH * 60;

  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const showNow = isToday && nowMin >= startH * 60 && nowMin <= endH * 60;

  const isEmpty = items.length === 0;

  return (
    <div className="rounded-[var(--p-radius-lg)] border border-[color:var(--p-border)] bg-[color:var(--p-card)] shadow-[var(--p-shadow)]" data-testid="planner-daily-timeline">
      <div className="flex items-center justify-between border-b border-[color:var(--p-line)] px-4 py-3">
        <SectionTitle icon={CalendarClock}>Schedule</SectionTitle>
        <button type="button" onClick={() => onNewEvent(dateStr)} data-testid="planner-timeline-add-event"
          className="n-focus inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[12px] font-semibold text-[color:var(--p-accent)] hover:bg-[color:var(--p-accent-bg)]"><Plus size={14} /> Event</button>
      </div>

      {untimed.length > 0 && (
        <div className="flex flex-wrap gap-1.5 border-b border-[color:var(--p-line)] px-4 py-2.5">
          <span className="mr-1 self-center text-[10px] font-semibold uppercase tracking-wider text-[color:var(--p-muted-2)]">Untimed</span>
          {untimed.map((i) => <span key={i.id} className="max-w-[220px]"><ItemChip item={i} onOpen={onOpenItem} /></span>)}
        </div>
      )}

      {isEmpty ? (
        <div className="flex flex-col items-center justify-center gap-2 px-6 py-14 text-center" data-testid="planner-daily-empty-state">
          <CalendarPlus size={26} className="text-[color:var(--p-muted-2)]" />
          <div className="font-display text-lg text-[color:var(--p-text)]">A clear day.</div>
          <div className="max-w-[260px] text-[12.5px] text-[color:var(--p-muted)]">Add an event or meeting, or just focus on your goals and tasks.</div>
          <button type="button" onClick={() => onNewEvent(dateStr)} className="n-focus mt-1 inline-flex items-center gap-1 rounded-lg border border-[color:var(--p-border)] px-3 py-1.5 text-[12.5px] font-semibold text-[color:var(--p-text-soft)] hover:border-[color:var(--p-accent)] hover:text-[color:var(--p-accent)]"><Plus size={14} /> New event</button>
        </div>
      ) : (
        <div className="relative flex px-2 py-2">
          <div className="w-[var(--p-time-rail)] shrink-0">
            {hours.map((h) => (
              <div key={h} style={{ height: HOUR_PX }} className="relative">
                <span className="p-tnum absolute -top-2 right-2 text-[10.5px] text-[color:var(--p-muted)]">{String(h).padStart(2, '0')}:00</span>
              </div>
            ))}
          </div>
          <div className="relative flex-1 p-ruled" style={{ height: (endH - startH) * HOUR_PX }}>
            {showNow && (
              <div className="pointer-events-none absolute left-0 right-1 z-20" style={{ top: ((nowMin - spanMin) / 60) * HOUR_PX }} data-testid="planner-now-indicator">
                <div className="flex items-center"><span className="h-2 w-2 -ml-1 rounded-full bg-[color:var(--p-accent)]" /><span className="h-px flex-1 bg-[color:var(--p-accent)]" /></div>
              </div>
            )}
            {placed.map(({ i, s, e, lane }) => {
              const top = ((s - spanMin) / 60) * HOUR_PX;
              const height = Math.max(((e - s) / 60) * HOUR_PX, 26);
              const width = `calc((100% - 8px) / ${laneCount})`;
              const left = `calc((100% - 8px) / ${laneCount} * ${lane})`;
              const isMeeting = i.kind === 'meeting';
              return (
                <button key={i.id} type="button" onClick={() => onOpenItem(i)} data-testid="planner-timeline-item"
                  style={{ top, height, width, left }}
                  className={`absolute overflow-hidden rounded-[10px] border px-2.5 py-1 text-left shadow-sm transition-shadow hover:z-10 hover:shadow-md ${isMeeting ? 'border-[color:var(--p-accent-soft)] bg-[color:var(--p-accent-bg)]' : 'border-[color:var(--p-border)] bg-[color:var(--p-card-2)]'}`}>
                  <div className="flex items-center gap-1">
                    {isMeeting ? <Users size={11} className="shrink-0 text-[color:var(--p-accent)]" /> : <Clock size={11} className="shrink-0 text-[color:var(--p-muted)]" />}
                    <span className="truncate text-[12.5px] font-semibold text-[color:var(--p-text)]">{i.title}</span>
                  </div>
                  <div className="flex items-center justify-between gap-1">
                    <span className="flex min-w-0 items-center gap-1">
                      <span className="p-tnum truncate text-[10.5px] text-[color:var(--p-muted)]">{fmtTimeRange(i.start_time, i.end_time)}</span>
                      <LinkedTasksBadge tasks={i.tasks} compact />
                    </span>
                    <ParticipantAvatars item={i} size={15} max={3} />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

const GoalSection = ({ title, goals, onToggle, onQuantity, onEdit }) => {
  if (!goals || goals.length === 0) return null;
  return (
    <div className="space-y-0.5">
      <div className="px-2.5 pb-0.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-[color:var(--p-muted-2)]">{title}</div>
      {goals.map((g) => <GoalRow key={g.id} goal={g} onToggle={onToggle} onQuantity={onQuantity} onEdit={onEdit} />)}
    </div>
  );
};

/* Collapsible Tasks group (Overdue / Today / Completed) with a live count in the
   header. Collapsed by default; expanding reveals the day's cards side-by-side
   in a responsive grid (full-parity cards). Renders nothing when empty. */
const TaskGroup = ({ id, label, tone, tasks, cardHandlers, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  if (!tasks || tasks.length === 0) return null;
  const toneCls = tone === 'danger'
    ? 'text-[color:var(--p-danger)]'
    : tone === 'success'
      ? 'text-[color:var(--p-success)]'
      : 'text-[color:var(--p-accent)]';
  return (
    <div data-testid={`planner-daily-task-group-${id}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid={`planner-daily-task-group-${id}-toggle`}
        className="n-focus mb-1 flex w-full items-center gap-1.5 rounded-md px-1.5 py-1.5 text-left transition-colors hover:bg-[color:var(--p-accent-bg)]/40"
      >
        <ChevronRight size={14} className={`shrink-0 text-[color:var(--p-muted-2)] transition-transform duration-200 ${open ? 'rotate-90' : ''}`} aria-hidden="true" />
        <span className={`text-[11px] font-semibold uppercase tracking-[0.12em] ${toneCls}`}>{label}</span>
        <span
          data-testid={`planner-daily-task-group-${id}-count`}
          className="p-tnum ml-auto inline-flex min-w-[20px] items-center justify-center rounded-full border border-[color:var(--p-border)] bg-[color:var(--p-card-2)] px-1.5 py-0.5 text-[10.5px] font-bold text-[color:var(--p-text-soft)]"
        >
          {tasks.length}
        </span>
      </button>
      {open && (
        <div className="grid grid-cols-1 gap-3 pb-2 pt-0.5 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4" data-testid={`planner-daily-task-group-${id}-list`}>
          {tasks.map((t) => (
            <RichTaskCard key={t.id} task={t}
              onOpen={cardHandlers.onOpen} onQuickActions={cardHandlers.onQuickActions} onOpenSubtasks={cardHandlers.onOpenSubtasks} />
          ))}
        </div>
      )}
    </div>
  );
};

export default function DailyPlannerView({ data, onToggleGoal, onQuantityGoal, onOpenTask, onOpenNote, onOpenItem, onEditGoal, onNewEvent, onNewGoal, onSaveDayNote, onOpenDate, cardHandlers }) {
  const goals = data.goals || { daily: [], weekly: [], monthly: [] };
  const hasGoals = (goals.daily.length + goals.weekly.length + goals.monthly.length) > 0;
  const taskGroups = useMemo(() => {
    const overdue = [], today = [], completed = [];
    for (const t of (data.tasks || [])) {
      if (t.completed) completed.push(t);
      else if (t.carried_over || (t.due_date && data.today && t.due_date < data.today)) overdue.push(t);
      else today.push(t);
    }
    const prioRank = { high: 0, medium: 1, normal: 2, low: 3 };
    overdue.sort((a, b) => (a.due_date || '').localeCompare(b.due_date || ''));
    today.sort((a, b) => (prioRank[a.priority] ?? 2) - (prioRank[b.priority] ?? 2));
    completed.sort((a, b) => (b.completed_at || '').localeCompare(a.completed_at || ''));
    return { overdue, today, completed };
  }, [data.tasks, data.today]);
  const totalTasks = taskGroups.overdue.length + taskGroups.today.length + taskGroups.completed.length;
  const notes = data.notes || [];

  return (
    <div className="p-fade-in mx-auto max-w-6xl space-y-4">
      {/* Tasks — ABOVE the schedule, full-width. Groups collapsed by default so
          the whole plan stays visible without scrolling; expand to a grid. */}
      <div className="rounded-[var(--p-radius-lg)] border border-[color:var(--p-border)] bg-[color:var(--p-card)] p-4 shadow-[var(--p-shadow)]" data-testid="planner-daily-tasks-panel">
        <SectionTitle icon={ListTodo} action={totalTasks ? <span className="p-tnum text-[11px] font-semibold text-[color:var(--p-muted-2)]" data-testid="planner-daily-tasks-total">{totalTasks}</span> : null}>Tasks</SectionTitle>
        {totalTasks ? (
          <div className="space-y-1.5" data-testid="planner-daily-tasks-list">
            <TaskGroup id="overdue" label="Overdue" tone="danger" tasks={taskGroups.overdue} cardHandlers={cardHandlers} />
            <TaskGroup id="today" label="Today" tone="accent" tasks={taskGroups.today} cardHandlers={cardHandlers} defaultOpen />
            <TaskGroup id="completed" label="Completed" tone="success" tasks={taskGroups.completed} cardHandlers={cardHandlers} />
          </div>
        ) : (
          <div className="rounded-[var(--p-radius-md)] bg-[color:var(--p-card-2)] px-3 py-5 text-center text-[12.5px] text-[color:var(--p-muted)]">No tasks due.</div>
        )}
      </div>

      {/* Below Tasks: Schedule (left) + Goals / Day note / Notes (right) */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
        {/* Left column: timeline */}
        <div className="order-2 lg:order-1">
          <AllDayStrip events={data.all_day_events} onOpenItem={onOpenItem} />
          <Timeline events={data.events} meetings={data.meetings} onOpenItem={onOpenItem} onNewEvent={onNewEvent} dateStr={data.date} isToday={data.date === data.today} />
        </div>

        {/* Right column: goals, day note, notes */}
        <div className="order-1 space-y-4 lg:order-2">
          {/* Mini calendar — jump to any day */}
          <PlannerMiniCalendar mode="day" anchor={data.date} today={data.today} onPick={onOpenDate} />

          {/* Goals */}
          <div className="rounded-[var(--p-radius-lg)] border border-[color:var(--p-border)] bg-[color:var(--p-card)] p-4 shadow-[var(--p-shadow)]" data-testid="planner-daily-goals-panel">
            <SectionTitle icon={Target} action={<button type="button" onClick={onNewGoal} data-testid="planner-daily-add-goal" className="n-focus inline-flex items-center gap-1 rounded-lg px-1.5 py-0.5 text-[11.5px] font-semibold text-[color:var(--p-accent)] hover:bg-[color:var(--p-accent-bg)]"><Plus size={13} /> Goal</button>}>Goals</SectionTitle>
            {hasGoals ? (
              <div className="space-y-2.5">
                <GoalSection title="Daily" goals={goals.daily} onToggle={onToggleGoal} onQuantity={onQuantityGoal} onEdit={onEditGoal} />
                <GoalSection title="Weekly" goals={goals.weekly} onToggle={onToggleGoal} onQuantity={onQuantityGoal} onEdit={onEditGoal} />
                <GoalSection title="Monthly" goals={goals.monthly} onToggle={onToggleGoal} onQuantity={onQuantityGoal} onEdit={onEditGoal} />
              </div>
            ) : (
              <div className="rounded-[var(--p-radius-md)] bg-[color:var(--p-card-2)] px-3 py-5 text-center text-[12.5px] text-[color:var(--p-muted)]">No active goals for this day.</div>
            )}
          </div>

          {/* Day note (Planner-native, autosaves) */}
          <DayNoteCard date={data.date} initial={data.day_note} onSave={onSaveDayNote} />

          {/* Notes activity */}
          {notes.length > 0 && (
            <div className="rounded-[var(--p-radius-lg)] border border-[color:var(--p-border)] bg-[color:var(--p-card)] p-4 shadow-[var(--p-shadow)]" data-testid="planner-daily-notes-panel">
              <SectionTitle icon={BookOpen}>Notes activity</SectionTitle>
              <div className="space-y-0.5">{notes.map((n, idx) => <NoteRow key={`${n.note_id}-${n.activity}-${idx}`} note={n} onOpen={onOpenNote} />)}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
